import toolIndex from "toolConstants";
import ReducersIndex from 'reduxModules/ducks';
import { getDeviceLayouts } from "reduxModules/ducks/room";
import { compact, every, has, isEmpty, isEqual, keys, omitBy, pick, some } from "lodash";
import { getStoreProp } from "utilities/string";
import { getBoundingBox } from "reduxModules/ducks/ui";
import validate from "toolConstants/conferenceroomDesigner/validation";
import { getEquipmentSummary } from "reduxModules/ducks/container";


/**
* @module calculateDevicesMiddleware
* @description Redux middleware for managing device calculations and layouts in room design.
* 
* @function calculateDevicesMiddleware
* @description Handles device-related actions and triggers recalculations when needed.
* 
* @param {Object} store - Redux store
* @returns {Function} Redux middleware function
* 
* @description
* This middleware:
* 1. Manages local device calculations when enabled
* 2. Handles device customization states
* 3. Coordinates updates between different device types
* 4. Manages plot data generation and updates
* 
* Key functionalities:
* - Device state management:
*   - Handles microphone, speaker, and camera states
*   - Manages device customization flags
*   - Coordinates between dependent devices (e.g., videobar affecting both audio and video)
* 
* - Plot data management:
*   - Clears and regenerates plot data as needed
*   - Controls heatmap visibility
*   - Ensures plot data consistency
* 
* - Customization handling:
*   - Manages device customization states
*   - Handles resets when room parameters change
*   - Shows appropriate warnings for customization changes
* 
* - Table interactions:
*   - Manages microphone changes based on table presence
*   - Handles coverage adjustments for table-focused devices
* 
* @example
* // Middleware usage in store configuration
* const store = configureStore({
*   reducer: rootReducer,
*   middleware: (getDefaultMiddleware) =>
*     getDefaultMiddleware().concat(calculateDevicesMiddleware)
* });
* 
* @note
* - Only processes calculations when localCalc is enabled
* - Requires area to be defined before processing
* - Uses setTimeout to ensure state updates complete before recalculations
* - Maintains customization states across updates
*/
let saveTimer;
let layoutErrors = [];
const calculateDevicesMiddleware = ({ dispatch, getState }) => next => action => {
  const state = getState();

  const constants = toolIndex[state.ui?.tool]?.constants;
  // Get flag to allow local caculation
  const localCalc = constants?.tool?.localCalc ?? false;
  const areaDefined = !isEmpty(getBoundingBox(state))
  if (localCalc && areaDefined) {

    const areaParams = constants?.areaParams ?? [];
    const currentDevice = state.container.currentDevice;
    const currentArea = state.container?.activeTab;
    const areaState = state.container?.areas[currentArea] ?? {};
    if (isEmpty(areaState)) return
    const currentAreaState = pick(areaState, areaParams);
    const {
      microphones: { model: micModel },
      speakers: { model: spkrModel },
      cameras: { model: cameraModel },
      customized: { microphones: isMicCustom = false, speakers: isSpkrCustom = false, cameras: isCameraCustom = false } = {},
    } = areaState;

    const {
      microphones: { [micModel.replace('mic', '_mic')]: { isTableMic = false, isBarMic = false, isVideoBar = false } },
      speakers: { [spkrModel]: { isBarSpkr = false } },
      cameras: { [cameraModel || 'dummy']: { isBarCamera = false } = {} } = {},
    } = constants.equipment

    let otherDevices = []; 
    
    switch (true) {
      // update area on forced mic changes
      case action.type === ReducersIndex.uiReducers.closeWarning.type:
        const hasError = some(['1100', '1110', '1200', '1300', '1400', '1050'], key => has(state.ui.error, key)); //!isEmpty(intersection(keys(state.ui.error), ['1100', '1110', '1200', '1300', '1400']));
        const hasUpdate = !isEmpty(state.container.areaToUpdate);
        
        if (hasUpdate && hasError) {
          dispatch(ReducersIndex.container.updateArea(true));
          // Reset table error after table is removed 
          if (has(state.ui.error, '1050')) dispatch(ReducersIndex.uiReducers.setError({ code: 1050 }));
        };
        break;
      case action.type === ReducersIndex.container.setMicrophones.type && has(action.payload, 'model'):
        dispatch(ReducersIndex.uiReducers.setShowDevices(false));
        // Set the hasBar property depending on the new mic model 
        const newModel = action.payload.model;
        const newMicIsBar = constants.equipment.microphones[newModel.replace('mic', '_mic')]?.isBarMic ?? false;
        dispatch(ReducersIndex.container.setHasBar(newMicIsBar));

      // intentional fall through
      case action.type === ReducersIndex.uiReducers.setActiveTab.type:
        dispatch(ReducersIndex.uiReducers.setShowHeatmap(false));
        break;

      case action.type === ReducersIndex.uiReducers.setDeviceIsMoving.type:
        if (!action.payload) {
          otherDevices = compact([
            currentDevice !== 'microphones' && (currentDevice === 'speakers' ? isBarSpkr : isBarCamera) && 'microphones',
            currentDevice !== 'speakers' && (currentDevice === 'microphones' ? isBarMic : isBarCamera) && 'speakers',
            currentDevice !== 'cameras' && isVideoBar && 'cameras'
          ]);
        };        
        dispatch(ReducersIndex.uiReducers.setShowHeatmap(false));
        // Clear plotData
        const plotsToClear = [currentDevice, ...otherDevices].reduce((acc, curr) => {
          acc[curr] = { plotData: {} }
          return acc
        }, {});
        dispatch(ReducersIndex.container.updateDevice(plotsToClear));

      // intentional fall through
      case action.type === ReducersIndex.container.addDevice.type:
      // intentional fall through
      case action.type === ReducersIndex.container.removeDevice.type:
        const customParameters = [currentDevice, ...otherDevices].reduce((acc, curr) => {
          acc[curr] = constants.customSettings[curr]
          return acc
        }, {})
        if (!isEmpty(customParameters)) {
          dispatch(ReducersIndex.container.setCustomized(customParameters));
        };
        break;

      // Clear all customizations when area dimensions change 
      // This function only passes the dimenssion change to be procvessed
      // The actual resetting happens in areaResrt (in logic)
      case action.type === ReducersIndex.container.setDimensions.type:
      // intentional fall through
      case action.type === ReducersIndex.container.setCeilingHeight.type:

        if (isMicCustom || isSpkrCustom || isCameraCustom) {
          const parameter = getStoreProp(action.type);
          const payload = {
            area: currentArea,
            update: {
              parameter: parameter,
              value: action.payload
            },
          };
          dispatch(ReducersIndex.container.setAreaToUpdate(payload));

          const devices = compact([
            isMicCustom && "microphones",
            isSpkrCustom && "speakers",
            isCameraCustom && "cameras"
          ])

          const error = {
            code: 100,
            msg: `Changing room dimensions will reset ${devices.length === 3 ? 'all devices' : devices.join(' and ')} to the recommended solution.`,
            reset: true,
          };
          dispatch(ReducersIndex.uiReducers.setError(error));
          return
        };
        break;


      case action.type === ReducersIndex.container.setTable.type:
        if ('shape' in action.payload) {
          const newShape = Boolean(action.payload.shape);
          if (!isMicCustom) dispatch(ReducersIndex.container.setMicrophones({ model: (isTableMic && !newShape ? 'ceilingmic' : micModel), coverage: { full_room: !newShape, focused: newShape } }))
        }

        break;

      case action.type === ReducersIndex.container.setMicrophones.type:
        if (isMicCustom && (has(action.payload, 'model') || has(action.payload, 'coverage'))) {
          const parameter = getStoreProp(action.type);
          const payload = {
            area: currentArea,
            update: {
              parameter: parameter,
              value: action.payload
            },
          };
          dispatch(ReducersIndex.container.setAreaToUpdate(payload));

          const error = {
            code: 100,
            msg: `Changing microphone ${has(action.payload, 'model') ? 'style' : 'coverage'} will reset microphones to the recommended solution.`,
            reset: true
          };
          dispatch(ReducersIndex.uiReducers.setError(error));
          return
        };
        break;

      case action.type === ReducersIndex.container.setSpeakers.type:
        if (isSpkrCustom && (has(action.payload, 'model') || has(action.payload, 'coverage'))) {
          const parameter = getStoreProp(action.type);
          const payload = {
            area: currentArea,
            update: {
              parameter: parameter,
              value: action.payload
            },
          };
          dispatch(ReducersIndex.container.setAreaToUpdate(payload));
          const error = {
            code: 100,
            msg: `Changing speaker ${has(action.payload, 'model') ? 'style' : 'coverage'} will reset speakers to the recommended solution.`,
            reset: true
          };
          dispatch(ReducersIndex.uiReducers.setError(error));
          return
        };
        break;

      // catch summary table to verify if devio is selected and can be used 
      case action.type === ReducersIndex.container.updateDevice.type:
        validate.productFamily(dispatch, state)
        break;



      default:
    };


    // Trigger the update 
    next(action)

    let debounce = 10
    if (action.type === ReducersIndex.container.addDevice.type) {
      clearTimeout(saveTimer)
      debounce = 1000
    }

    saveTimer = setTimeout(() => {
      let state = getState();

      // get the updated area
      const areaState = state.container?.areas[currentArea] ?? {};
      if (isEmpty(areaState)) return
      const updatedAreaState = pick(areaState, areaParams);
      const areaChanged = !isEqual(currentAreaState, updatedAreaState);
      const currentDevice = state.container.currentDevice;
      const currentView = state.ui.currentView;
      const plotDevices = constants.devices;

      // Check if plots need to be updated
      const needsPlotData = ['microphones', 'speakers', 'cameras'].reduce((acc, curr) => {
        acc[curr] = has(areaState[curr], 'plotData') ? isEmpty(areaState[curr].plotData) : true
        return acc
      }, {});
      if (!state.ui.showHeatmap && [...plotDevices, 'acoustics'].includes(currentView)) dispatch(ReducersIndex.uiReducers.setShowHeatmap(true));

      // Stop if there are any error to prevent infinite loops
      const error = state.ui.errorState;
      const deviceIsMoving = state.ui.deviceIsMoving;
      const isCollisionError = some(['1300'], key => has(state.ui.error, key)) //!isEmpty(intersection(keys(state.ui.error), ['1110', '1300'])); //keys(state.ui.error).includes('1300');
      if (error && !isCollisionError) {  
        return};

      // Update devices as needed
      if (areaChanged || (currentDevice && needsPlotData[currentDevice]) || every(needsPlotData)) {   
        const {collisions = {microphones: [], speakers: []}, errors = [], ...deviceLayout} = getDeviceLayouts(state);
        const otherPlots = keys(omitBy(needsPlotData, (value, key) => key === currentDevice || !value));
        const filteredLayout = areaState.customized[currentDevice] ? pick(deviceLayout, [currentDevice, ...otherPlots]) : deviceLayout;
        dispatch(ReducersIndex.container.updateDevice(filteredLayout));
        dispatch(ReducersIndex.container.setCollisions(collisions));

        layoutErrors = errors.length > 0 ? [...errors] : [];
        
        // Refresh the state and set equipment list 
        state = getState();
        const equipmentList = getEquipmentSummary(state);
        dispatch(ReducersIndex.container.setEquipmentList(equipmentList));

        dispatch(ReducersIndex.uiReducers.setNeedsResave(true));

        dispatch(ReducersIndex.uiReducers.setShowDevices(true));
      };

 
      if (['microphones', 'speakers'].includes(currentDevice) && !isEmpty(layoutErrors) && !deviceIsMoving) {
        layoutErrors.forEach(error => {
          dispatch(ReducersIndex.uiReducers.setError(error))
          if (error.code === 1300) dispatch(ReducersIndex.uiReducers.setShowOverlaps(true));
        });
        
        layoutErrors = [];
      }

    }, debounce);



    return
  }

  else {
    next(action)
  }

};

export default calculateDevicesMiddleware;